import { allconfig } from '../config'
import { getAPIBaseURL } from './helpers'
import { postData } from './request'

const REF_COOKIE = 'rp_ref'
const CAMPAIGN_COOKIE = 'rp_campaign'
const DEVICE_COOKIE = 'rp_device'
const CLICK_SENT_COOKIE = 'rp_click_sent'
const VISITOR_COOKIE = 'rp_visitor'
const MAX_AGE_SECONDS = 2592000 // 30 days
const CAMPAIGN_ID = allconfig.referpay_campaign_id

const getCookie = (name) => {
  if (typeof document === 'undefined') return null
  const prefix = `${name}=`
  const cookies = document.cookie ? document.cookie.split(';') : []
  for (const part of cookies) {
    const trimmed = part.trim()
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length))
    }
  }
  return null
}

const setCookie = (name, value) => {
  if (typeof document === 'undefined') return
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${MAX_AGE_SECONDS}; SameSite=Lax`
}

const getOrCreateVisitorId = () => {
  let visitorId = getCookie(VISITOR_COOKIE)
  if (visitorId) return visitorId
  visitorId =
    (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
    `v_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
  setCookie(VISITOR_COOKIE, visitorId)
  return visitorId
}

const reportQualifiedClick = (referralCode, campaignId, deviceToken) => {
  if (campaignId !== CAMPAIGN_ID) return
  const sentKey = `${referralCode}:${campaignId}`
  if (getCookie(CLICK_SENT_COOKIE) === sentKey) return

  const visitorId = getOrCreateVisitorId()
  const url = `${getAPIBaseURL()}/employme/referpay/click`
  postData(
    url,
    { 'Content-Type': 'application/json' },
    { referralCode, campaignId, visitorId, deviceToken }
  ).then((requestResult) => {
    const status = requestResult?.result?.status
    if (!requestResult?.error && status >= 200 && status < 300) {
      setCookie(CLICK_SENT_COOKIE, sentKey)
    }
  })
}

export const captureReferralFromUrl = () => {
  if (typeof window === 'undefined') return

  const params = new URLSearchParams(window.location.search)
  const ref = params.get('ref')
  const campaignId = params.get('rp_campaign')
  const deviceToken = params.get('rp_device')

  if (ref && campaignId) {
    setCookie(REF_COOKIE, ref)
    setCookie(CAMPAIGN_COOKIE, campaignId)
    if (deviceToken) setCookie(DEVICE_COOKIE, deviceToken)
    reportQualifiedClick(ref, campaignId, deviceToken || getCookie(DEVICE_COOKIE))
  }
}

export const getReferralAttribution = () => {
  const referralCode = getCookie(REF_COOKIE)
  const campaignId = getCookie(CAMPAIGN_COOKIE)
  const deviceToken = getCookie(DEVICE_COOKIE)

  if (referralCode && campaignId) {
    // Same visitorId used for the click conversion — required for signup/onboarding tasks
    return {
      referralCode,
      campaignId,
      deviceToken: deviceToken || null,
      visitorId: getOrCreateVisitorId()
    }
  }

  return null
}

export const appendReferralAttribution = (payload) => {
  const referral = getReferralAttribution()
  if (!referral) return payload

  if (payload instanceof URLSearchParams) {
    payload.set('referralCode', referral.referralCode)
    payload.set('campaignId', referral.campaignId)
    if (referral.deviceToken) payload.set('deviceToken', referral.deviceToken)
    if (referral.visitorId) payload.set('visitorId', referral.visitorId)
    return payload
  }

  if (typeof FormData !== 'undefined' && payload instanceof FormData) {
    payload.append('referralCode', referral.referralCode)
    payload.append('campaignId', referral.campaignId)
    if (referral.deviceToken) payload.append('deviceToken', referral.deviceToken)
    if (referral.visitorId) payload.append('visitorId', referral.visitorId)
    return payload
  }

  return {
    ...payload,
    referralCode: referral.referralCode,
    campaignId: referral.campaignId,
    ...(referral.deviceToken ? { deviceToken: referral.deviceToken } : {}),
    ...(referral.visitorId ? { visitorId: referral.visitorId } : {})
  }
}
